import { Document, DocumentRow, DocumentRowValue } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { POLineItemMatch } from '@/types/po-match'
import { WorkflowState } from '@/types/workflows'
import {
  INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
  INVOICE_TOTAL_INFO_TYPE_ID,
  INVOICE_VENDOR_INFO_TYPE_ID,
  PURCHASE_ORDER_NUMBER_INFO_TYPE_ID,
} from '@/utils/info-types'
import { getRowValuesMainValues } from '@/components/validation/useGetValueData'

/** A line on a related invoice and the PO line item it's matched to. */
export type RelatedInvoiceTie = {
  matchId: string
  invoiceRowId: string
  description: string | null
  amount: string | null
  poId: string
  poLineItemId: string
}

/** One line on a related invoice, with whatever PO lines it's tied to (often none). */
export type RelatedInvoiceLine = {
  rowId: string
  description: string | null
  amount: string | null
  ties: RelatedInvoiceTie[]
}

/** Another invoice whose lines are matched to a PO the focused invoice is also matched to. */
export type RelatedInvoice = {
  id: string
  name: string
  projectId: string
  workflowState?: WorkflowState
  vendorName: string | null
  invoiceTotal: string | null
  poNumber: string | null
  /** In the invoice's own line order. */
  ties: RelatedInvoiceTie[]
  /** Every line on the invoice, tied or not, in its own line order. */
  lines: RelatedInvoiceLine[]
}

/** The info types of the header values a related invoice card shows. */
export const RELATED_INVOICE_HEADER_INFO_TYPE_IDS = [
  INVOICE_VENDOR_INFO_TYPE_ID,
  INVOICE_TOTAL_INFO_TYPE_ID,
  PURCHASE_ORDER_NUMBER_INFO_TYPE_ID,
]

/** The info types of the line values a related invoice card shows. */
export const RELATED_INVOICE_LINE_INFO_TYPE_IDS = [
  INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
]

type BuildRelatedInvoicesOptions = {
  /** Must be fetched with `fields__include: 'document_row'` — a match without its row can't be tied to an invoice. */
  matches: POLineItemMatch[]
  documents: Document[]
  /** Each document's base-grid row, which holds its header values. */
  baseRows: DocumentRow[]
  /** Every line-item row of the documents. A tied row missing here (the project has no line fields, say) still gets a line. */
  lineRows: DocumentRow[]
  rowValues: DocumentRowValue[]
  /**
   * The fields of every related invoice's project. Each project has its own
   * field ids, so a value is matched to what it shows by its field's info type.
   */
  fields: Pick<ProjectGridField, 'id' | 'info_type_id'>[]
  workflowStates: WorkflowState[]
}

/**
 * Groups line item matches by the invoice they belong to and fills in each
 * invoice's header values. Invoices missing from `documents` (e.g. not visible
 * to this user) are left out. Sorted by invoice name.
 */
export function buildRelatedInvoices({
  matches,
  documents,
  baseRows,
  lineRows,
  rowValues,
  fields,
  workflowStates,
}: BuildRelatedInvoicesOptions): RelatedInvoice[] {
  const infoTypeIdByFieldId = new Map(
    fields.map((field) => [field.id, field.info_type_id]),
  )
  const valueByRowAndInfoType = new Map<string, string | null>()
  rowValues.forEach((rowValue) => {
    const infoTypeId = infoTypeIdByFieldId.get(rowValue.project_grid_field_id)
    if (!infoTypeId) return
    valueByRowAndInfoType.set(
      `${rowValue.document_row_id}:${infoTypeId}`,
      getRowValuesMainValues(rowValue).value || null,
    )
  })
  const valueFor = (rowId: string | undefined, infoTypeId: string) =>
    (rowId && valueByRowAndInfoType.get(`${rowId}:${infoTypeId}`)) || null

  const baseRowIdByDocumentId = new Map(
    baseRows.map((row) => [row.document_id, row.id]),
  )

  const lineRowsByDocumentId = Map.groupBy(lineRows, (row) => row.document_id)

  const matchesByDocumentId = Map.groupBy(
    matches.filter((match) => !!match.document_row),
    (match) => match.document_row?.document_id as string,
  )

  return documents
    .filter((document) => matchesByDocumentId.has(document.id))
    .map((document) => {
      const baseRowId = baseRowIdByDocumentId.get(document.id)
      const documentMatches = (
        matchesByDocumentId.get(document.id) ?? []
      ).toSorted(
        (a, b) =>
          (a.document_row?.row_number ?? 0) - (b.document_row?.row_number ?? 0),
      )
      const ties = documentMatches.map((match) => ({
        matchId: match.id,
        invoiceRowId: match.document_row_id,
        description: valueFor(
          match.document_row_id,
          INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
        ),
        amount: valueFor(
          match.document_row_id,
          INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
        ),
        poId: match.po_id,
        poLineItemId: match.po_line_item_id,
      }))
      const tiesByRowId = Map.groupBy(ties, (tie) => tie.invoiceRowId)
      const rowsById = new Map(
        [
          ...(lineRowsByDocumentId.get(document.id) ?? []),
          ...documentMatches.map((match) => match.document_row as DocumentRow),
        ].map((row) => [row.id, row]),
      )
      const lines = [...rowsById.values()]
        .toSorted((a, b) => a.row_number - b.row_number)
        .map((row) => ({
          rowId: row.id,
          description: valueFor(
            row.id,
            INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
          ),
          amount: valueFor(row.id, INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID),
          ties: tiesByRowId.get(row.id) ?? [],
        }))
      return {
        id: document.id,
        name: document.name,
        projectId: document.project_id,
        workflowState: workflowStates.find(({ id }) =>
          document.workflow_states_ids?.includes(id),
        ),
        vendorName: valueFor(baseRowId, INVOICE_VENDOR_INFO_TYPE_ID),
        invoiceTotal: valueFor(baseRowId, INVOICE_TOTAL_INFO_TYPE_ID),
        poNumber: valueFor(baseRowId, PURCHASE_ORDER_NUMBER_INFO_TYPE_ID),
        ties,
        lines,
      }
    })
    .toSorted((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
}

/** Anchor id for a related invoice card's tie summary row — where its lines start while it's collapsed. */
export function relatedInvoiceAnchorId(invoiceId: string) {
  return `related-invoice:${invoiceId}`
}

/** Anchor id for one line row in an expanded related invoice card. */
export function relatedLineAnchorId(rowId: string) {
  return `related-line:${rowId}`
}

/** A connector from a point on a related invoice card to a PO line. */
export type RelatedConnectorLine = {
  id: string
  invoiceId: string
  /** The `relatedInvoiceAnchorId` or `relatedLineAnchorId` the line starts from. */
  anchorId: string
  poLineItemId: string
}

/**
 * The connectors for the related invoice cards. An expanded card draws a line
 * from each tied line row to each PO line it's tied to. A collapsed card draws one line per PO line it's
 * tied to, all from its summary row, so the connection still shows.
 */
export function buildRelatedConnectorLines(
  invoices: RelatedInvoice[],
  expandedInvoiceIds: Set<string>,
): RelatedConnectorLine[] {
  return invoices.flatMap((invoice) => {
    if (expandedInvoiceIds.has(invoice.id)) {
      return invoice.ties.map((tie) => ({
        id: `related-${tie.matchId}`,
        invoiceId: invoice.id,
        anchorId: relatedLineAnchorId(tie.invoiceRowId),
        poLineItemId: tie.poLineItemId,
      }))
    }
    const poLineItemIds = [
      ...new Set(invoice.ties.map((tie) => tie.poLineItemId)),
    ]
    return poLineItemIds.map((poLineItemId) => ({
      id: `related-${invoice.id}-${poLineItemId}`,
      invoiceId: invoice.id,
      anchorId: relatedInvoiceAnchorId(invoice.id),
      poLineItemId,
    }))
  })
}
